// tslint:disable:max-file-line-count
import { IABExperiment } from '@cian/api-models/monolith-python/cian-api/site/v1/get-ab-experiments';
import { EMaxAuctionService } from '@cian/api-models/search-offers/v2/search-offers-desktop.data';
import { Config } from '@cian/config';
import { IHttpApi } from '@cian/http-api';
import { ILogger } from '@cian/logger';
import { mergeStyles } from '@cian/utils';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { checkTouchDevice } from '../../browser/utils/dom';
import { IOfferHelper, offerHelper } from '../presenter';
import { IJsonQuery } from '../types/json-query';
import { IOffer, ISimilar } from '../types/offer';
import { IUser } from '../types/user';
import { getPromotionsAnalyticsData } from '../utils/getPromotionsAnalyticsData';
import { trackGoToCardKP, trackSuburbanMultiOfferClick } from '../utils/tracking';
import { canUseHiddenBase } from '../utils/user';
import { CallbackModalContainer } from '../common/components/callback_modal';
import { Comments, ICommentsAPI } from '../common/components/comments';
import { GalleryImage } from '../common/components/gallery/image';
import { GalleryItemPreloader } from '../common/components/gallery/preloader';
import { HiddenBaseLabel } from '../common/components/hidden_base_label';
import { ILoginMotivationPopupAPI } from '../common/components/login_motivation_popup';
import { Map } from '../common/components/map';
import { IReportPopupAPI } from '../common/components/menu/more/report_popup';
import { IUserInfoAPI } from '../common/components/user-info-popup';
import { Address } from './components/address';
import { Agent } from './components/Agent';
import { AgentBrand } from './components/AgentBrand';
import { Buttons, IButtonsAPI } from './components/buttons';
import { Deadline } from './components/deadline';
import { Description } from './components/description';
import { JkName } from './components/jk_name';
import { LandArea } from './components/land-area';
import { LandStatus } from './components/land-status';
import { LeasePeriod } from './components/lease_period';
import { IPessimisationAPI, Pessimisation } from './components/pessimisation';
import { Photos } from './components/photos';
import { Price } from './components/price';
import { RightPanel } from '../common/components/RightPanel';
import { Title } from './components/title';
import { IComplaintsHelpers } from '../types/complaints';
import { getAgentPhone } from '../utils/getAgentPhone';
import { VillageInfoLink } from './components/VillageInfoLink';
import { TOfferCardTracking } from './tracking';
import * as styles from './offerCard.css';
import { HomeownerBlock } from '../components/HomeownerBlock';
import { ExtendedOfferLabel } from '../common/components/ExtendedOfferLabel';
import { PromotionsBlock } from '../components/PromotionsBlock';
import { slicePromotions } from '../utils/slicePromotions';
import { getSimilarLinkText } from '../utils/getSimilarLinkText';
import { isRSNewbuildingLabel } from '../utils/validators';
import { INewbuildingLabel } from '../types/newbuildingLabel';
import { IdenticalOffers } from '../components/IdenticalOffers';

export interface IOfferCardAPI {
  isPopupsOpened(): boolean;
  onFavoriteChange(isFavorite: boolean): void;
  onPageJumped(label?: string): void;
}

// Данные из запроса
export interface IOfferCardRequestProps {
  baseUrl: string;
  currentPath: string | undefined;
  currentSubdomain: string;
  experiments: IABExperiment[];
  isSimilar: boolean;
  jsonQuery: IJsonQuery;
  offer: IOffer;
  position: number;
  qsToUris?: { [key: string]: string };
  suggestionDistancesSeoText: string | undefined;
  user: IUser | null;
  isLightView?: boolean;
}

// Свойства компонентов, которые прокидываются в фрагмент в микросервисе
export interface IOfferCardComponentsProps {
  buttons: IButtonsAPI;
  comments: ICommentsAPI;
  loginMotivationPopup: ILoginMotivationPopupAPI;
  offerCard: IOfferCardAPI;
  pessimisation: IPessimisationAPI;
  reportPopup: IReportPopupAPI;
  userInfo: IUserInfoAPI;
  mortgageBanner?: React.ReactNode | undefined;
  tracking?: Partial<TOfferCardTracking>;
  favoritesLimitForUnauthUser?: number;
  isTitleExperimentActive?: boolean;
  excludedUtilitiesTermsRegions: number[];
}

export interface IOfferCardOwnProps {
  logger: ILogger;
  httpApi: IHttpApi;
  config: Config;
}

export interface IOfferCardProps extends IOfferCardRequestProps, IOfferCardOwnProps {
  api: IOfferCardComponentsProps;
  defaultPhone?: string;
  defaultEmail?: string;
  showPushNotification?: boolean;
  isOnHideAnimation?: boolean;
  isHideOfferFetching?: boolean;
  complaintsHelpers: IComplaintsHelpers;
  /** Перечень регионов ранжированного поиска */
  mlSearchRegions?: number[];
  /** ML-ранжирование, без проверки региона  */
  isMlSearchForAll?: boolean;
  /** Максимальная ставка аукциона на выдаче */
  maxAuctionBet?: number;
  /** Максимальная услуга выдачи */
  maxAuctionService?: EMaxAuctionService;
  /** Возможность получить ссылку на контейнер */
  containerRef?: React.RefObject<HTMLDivElement>;
  isPromotionsInOffersActive?: boolean;
  isOfferInSavedSearch?: boolean;
  queryString?: string | null;
  isCianAgencyExperimentActive?: boolean;
}

export interface IOfferCardState {
  isTouchDevice: boolean;
  galleryControlsHidden: boolean;
  isCallbackModalOpened: boolean;
  isMapActive: boolean;
  isMapLoaded: boolean;
  isOfferHidden: boolean;
  isCommentAreaOpened: boolean;
  isComplainOpen: boolean;
  areIdenticalOffersShown: boolean;
}

export interface IOfferCardContext {
  api: IOfferCardComponentsProps;
  baseUrl: string;
  currentSubdomain: string;
  defaultEmail: string;
  defaultPhone: string;
  experiments: IABExperiment[];
  httpApi: IHttpApi;
  isSimilar: boolean;
  // TODO: временное решение
  jsonQuery: IJsonQuery;
  logger: ILogger;
  offer: IOffer;
  offerHelper: IOfferHelper;
  user: IUser | null;
  /** Перечень регионов ранжированного поиска */
  mlSearchRegions: number[] | null;
  /** ML-ранжирование, без проверки региона  */
  isMlSearchForAll: boolean | null;
  /** Максимальная ставка аукциона на выдаче */
  maxAuctionBet?: number;
  /** Максимальная услуга выдачи */
  maxAuctionService?: EMaxAuctionService;
  /** Минимальная ставка для Московского региона */
  auctionMinBet?: number;
  /** Максимальная разрешённая ставка аукциона */
  auctionMaxBet?: number;
  /** Показывать ли бейдж от застройщика для загородных застройщиков */
  countrysideOffersBuilderBadge?: boolean;
  /** Валидировать ли шаг ставки */
  validateStep: boolean;
  queryString?: string | null;
  newbuildingLabel: INewbuildingLabel | null;
  onlineShowEnabled: boolean;
}

export class OfferCard extends React.Component<IOfferCardProps, IOfferCardState> {

  // tslint:disable-next-line:no-any
  public static childContextTypes: { [key in keyof IOfferCardContext]: PropTypes.Requireable<any> } = {
    api: PropTypes.object,
    baseUrl: PropTypes.string,
    currentSubdomain: PropTypes.string,
    defaultEmail: PropTypes.string,
    defaultPhone: PropTypes.string,
    experiments: PropTypes.array,
    httpApi: PropTypes.object,
    isMlSearchForAll: PropTypes.bool,
    isSimilar: PropTypes.bool,
    jsonQuery: PropTypes.object,
    logger: PropTypes.object,
    mlSearchRegions: PropTypes.array,
    offer: PropTypes.object,
    offerHelper: PropTypes.object,
    user: PropTypes.object,
    maxAuctionBet: PropTypes.number,
    maxAuctionService: PropTypes.string,
    auctionMinBet: PropTypes.number,
    auctionMaxBet: PropTypes.number,
    countrysideOffersBuilderBadge: PropTypes.bool,
    validateStep: PropTypes.bool,
    queryString: PropTypes.string,
    newbuildingLabel: PropTypes.object,
    onlineShowEnabled: PropTypes.bool,
  };

  public constructor(props: IOfferCardProps) {
    super(props);

    this.state = {
      galleryControlsHidden: true,
      isCallbackModalOpened: false,
      isCommentAreaOpened: false,
      isComplainOpen: false,
      isMapActive: false,
      isMapLoaded: false,
      isOfferHidden: false,
      isTouchDevice: false,
      areIdenticalOffersShown: false,
    };
  }

  public getChildContext(): IOfferCardContext {
    const {
      config,
      api,
      baseUrl,
      currentSubdomain,
      defaultEmail = '',
      defaultPhone = '',
      experiments,
      httpApi,
      isSimilar,
      jsonQuery,
      logger,
      offer,
      user,
      maxAuctionBet,
      maxAuctionService,
      queryString,
    } = this.props;

    const auctionMaxBet = offer.auction ? offer.auction.maxAvailableBet : 99;
    const auctionMinBetForMoscowRegion = Number(config.get<string>('auction.minBetForMoscowRegion'));
    const regionId = offer.geo && offer.geo.address && offer.geo.address.length ? offer.geo.address[0].id : 0;
    const isMoscowRegion = [1, 4593].includes(regionId);
    const auctionMinBet = isMoscowRegion ? auctionMinBetForMoscowRegion : 1;
    const newbuildingLabel = config.get('newbuildingLabels');

    return {
      api,
      baseUrl,
      currentSubdomain,
      defaultEmail,
      defaultPhone,
      experiments,
      httpApi,
      isMlSearchForAll: config.get<boolean>('isMlSearchForAll.Enabled') || false,
      isSimilar,
      jsonQuery,
      logger,
      mlSearchRegions: config.get<number[]>('mlSearchRegions') || [],
      offer,
      offerHelper: offerHelper(offer),
      user,
      maxAuctionBet,
      maxAuctionService,
      auctionMinBet,
      auctionMaxBet,
      countrysideOffersBuilderBadge: config.get<boolean>('Countryside.OffersBuilderBadge.Enabled') || false,
      validateStep: config.get<boolean>('validateStep') || false,
      queryString,
      newbuildingLabel: isRSNewbuildingLabel(newbuildingLabel, logger) ? newbuildingLabel as INewbuildingLabel : null,
      onlineShowEnabled: Boolean(config.get<boolean>('onlineShow.isEnabled')),
   };
  }

  public componentDidMount() {
    if (window) {
      this.setState({
        isTouchDevice: checkTouchDevice(),
      });
    }
  }

  public render() {
    const {
      api,
      offer,
      isOnHideAnimation,
      containerRef,
      isPromotionsInOffersActive,
      isOfferInSavedSearch,
    } = this.props;
    const agentPhone = getAgentPhone(offer);
    const callbackSettings = offer.workTimeInfo || {};
    const leftPhoto = offer.photos.length > 1 ? offer.photos[1] : null;
    const rightPhoto = offer.photos.length > 2 ? offer.photos[2] : null;
    const slicedPromotions = offer.kp && offer.kp.promotions && slicePromotions(offer.kp.promotions);
    const promotionAnalytics = getPromotionsAnalyticsData(offer.id, offer.kp);

    return (
      <div
        {...mergeStyles([
          styles['offer-container'],
          this.state.isOfferHidden && styles['offer-hidden'],
          isOnHideAnimation && styles['hide-offer-animation'],
          this.props.offer.isTop3 && styles['top3'],
          offer.isColorized && styles['colorized'],
          this.props.isLightView ? styles['light'] : null,
        ])}
        ref={containerRef}
      >
        <div
          {...mergeStyles([
            styles['container'],
            this.state.isTouchDevice && styles['touch-device'],
            this.checkSimilarLink() && styles['offer-similar'],
            this.props.api.offerCard.isPopupsOpened() && styles['offer-unclickable'],
          ])}
          onClick={this.handleOfferCardClick}
          onMouseEnter={this.handleOfferCardMouseEnter}
          onMouseLeave={this.handleOfferCardMouseLeave}
        >
          <div className={styles['main-container']}>
            <div {...mergeStyles([
              styles['media'],
              offer.isTop3 && styles['media-top3'],
            ])}>
              {isPromotionsInOffersActive && slicedPromotions &&
              <PromotionsBlock
                promotions={slicedPromotions}
                villageUrl={offer.fullUrl}
                analyticsData={promotionAnalytics}
              />
              }
              <Photos
                offerCardApi={this.props.api.offerCard}
                offer={this.props.offer}
                galleryControlsHidden={this.state.galleryControlsHidden}
                trackPhotoSlideChanged={api.tracking && api.tracking.trackPhotoSlideChanged}
                photoContainerStyle={offer.isTop3 ? styles['photos-container'] : ''}
                layoutPhotoStyle={offer.fromDeveloper ? styles['layout_image'] : undefined}
                isOfferInSavedSearch={isOfferInSavedSearch}
              />
              {offer.isTop3 &&
              <div className={styles['additional-photos']}>
                {leftPhoto &&
                <div className={styles['left-photo']}>
                  <GalleryItemPreloader>
                    <GalleryImage
                      src={leftPhoto.thumbnailUrl}
                      alt=""
                    />
                  </GalleryItemPreloader>
                </div>
                }
                {rightPhoto &&
                <div className={styles['right-photo']}>
                  <GalleryItemPreloader>
                    <GalleryImage
                      src={rightPhoto.thumbnailUrl}
                      alt=""
                    />
                  </GalleryItemPreloader>
                </div>
                }
              </div>
              }
            </div>
            <div className={styles['main']}>
              {this.renderInfo()}
              <div className={styles['panels-container']}>
                {this.renderLeftPanel()}
                <RightPanel />
              </div>
            </div>
          </div>
          {
            (this.state.isCommentAreaOpened || offer.notes.offer || offer.notes.realtor) && (
              <div onClick={e => e.stopPropagation()} className={styles['sub-block']}>
                <Comments
                  offer={offer}
                  isSimplified
                  onCompletion={this.handleCommentingCompletion}
                  {...api.comments}
                />
              </div>
            )
          }
          {this.renderSimilarLink()}
        </div>
        {offer.geo && offer.geo.coordinates && (
          <div {...mergeStyles([styles['map-container'], this.state.isMapActive && styles['map-active']])} >
            <div className={styles['map']}>
              {this.state.isMapLoaded && <Map coordinates={offer.geo.coordinates}/>}
            </div>
          </div>
        )}
        {this.state.isCallbackModalOpened && !!agentPhone && (
          <CallbackModalContainer
            useOldWidget={offer.category !== 'newBuildingFlatSale'}
            gaLabel={offer.gaLabel || ''}
            agentPhone={agentPhone}
            {...callbackSettings}
            close={this.toggleCallbackModal}
            httpApi={this.props.httpApi}
          />
        )}
        {this.renderIdenticalOffers()}
      </div>
    );
  }

  private renderIdenticalOffers() {
    const { offer: { identicalOffers } } = this.props;
    const { areIdenticalOffersShown } = this.state;

    if (!identicalOffers || !areIdenticalOffersShown) {
      return null;
    }

    const { items, url } = identicalOffers;

    return (
      <IdenticalOffers
        items={items}
        showAllUrl={url}
        onClickCard={this.handleIdenticalOfferCardClick}
        onClickHideButton={this.handleHideIdenticalOffers}
        onClickShowAllButton={this.handleShowAllIdenticalOffers}
      />
    );
  }

  private renderInfo() {
    const { offer, user, position } = this.props;
    const { mortgageBanner, isTitleExperimentActive } = this.props.api;
    const { isPaid, isByHomeowner, extensionLabels, isPremium, isTop3 } = offer;

    // Любое Премиум-объявление или Платное + РЧ
    // Для Топ-3 есть TopOfferCard и SlonoTopCard
    const isAgentBrandShow = (isPremium || isPaid);

    return (
      <div className={styles['info-container']}>
        <div className={styles['info']}>
          <div className={`${styles['info-section']} ${styles['main-info']}`}>
            <div>
              <a onClick={this.handleTitleClick}
                 href={offer.fullUrl}
                 className={styles['header']}
                 target="_blank"
              >
                <Title />
              </a>
              <div onClick={e => e.stopPropagation()}>
                <JkName />
              </div>
              <Deadline />
              <LeasePeriod />
              <LandStatus />
              <LandArea />
              {user && canUseHiddenBase(user) && Boolean(offer.isInHiddenBase) && (
                <HiddenBaseLabel containerStyle={styles['base-label']}/>
              )}
            </div>
            <div className={styles['price-flex-container']}>
              <Price
                mortgageBanner={this.props.api.mortgageBanner}
                currentSubdomain={this.props.currentSubdomain}
                excludedUtilitiesTermsRegions={this.props.api.excludedUtilitiesTermsRegions}
              />
              {
                mortgageBanner
              }
            </div>
          </div>
          <div className={styles['info-section']}>
            <Address
              currentPath={this.props.currentPath}
              qsToUris={this.props.qsToUris}
               suggestionDistancesSeoText={this.props.suggestionDistancesSeoText}
            />
          </div>
          <ExtendedOfferLabel labels={extensionLabels} labelStyle={styles['label']}/>
          <Description
            {...mergeStyles(
              styles['info-section'],
              isTitleExperimentActive && styles['titled-description'],
              isTop3 && styles['top3-description'],
            )}
            isTitleExperimentActive={isTitleExperimentActive}
          />
          <VillageInfoLink
            kp={offer.kp}
            category={offer.category}
            onLinkClick={() => {
              trackGoToCardKP(offer, 'card_name_kp_in_newlist', position);
            }}
          />
          <Pessimisation
            onComplainStateChanged={(isComplainOpen) => this.setState({ isComplainOpen })} />
        </div>

        {isByHomeowner && <HomeownerBlock />}

        {!isByHomeowner && (
          (offer.user || offer.cianUserId) && isAgentBrandShow
            ? <AgentBrand position={this.props.position} isSimilar={this.props.isSimilar} />
            : <Agent />
        )}
      </div>
    );
  }

  private renderLeftPanel() {
    const { offer, complaintsHelpers, isHideOfferFetching, isOnHideAnimation } = this.props;
    const isPhoneHidden = !!offer.kp && !offer.kp.displayBuilderPhone;

    return (
      <Buttons
        position={this.props.position}
        isMapActive={this.state.isMapActive}
        onHideFromPrintClicked={this.handleHideFromPrintClick}
        onMapButtonClick={this.handleButtonMapClick}
        isCallbackVisible={!!getAgentPhone(offer)}
        onCallbackClick={this.toggleCallbackModal}
        offer={offer}
        containerClassName={styles['left-panel']}
        onCommentClick={this.handleCommentClick}
        buttonClassName={styles['button']}
        dynamicButtonClassName={styles['dynamic-button']}
        onFavoriteChange={this.props.api.offerCard.onFavoriteChange}
        isComplainOpen={this.state.isComplainOpen}
        onComplainClose={() => this.setState({ isComplainOpen: false })}
        isSimilar={this.props.isSimilar}
        isPhoneHidden={isPhoneHidden}
        isHideOfferFetching={isHideOfferFetching || isOnHideAnimation}
        isSimplified
        complaintsHelpers={complaintsHelpers}
        onIdenticalOffersClick={this.handleIdenticalOffersButtonClick}
      />
    );
  }

  private renderSimilarLink() {
    if (!this.checkSimilarLink()) {
      return null;
    }

    const { offer } = this.props;
    const { category, similar } = offer;
    const similarCount = similar && similar.count;

    if (!similarCount) {
      return null;
    }

    return (
      <a
        {...mergeStyles([
          styles['sub-block'],
          styles['similar'],
        ])}
        href={(offer.similar as ISimilar).url}
        rel="nofollow noopener"
        target="_blank"
        onClick={this.handleSimilarClick}
      >
        {getSimilarLinkText(similarCount, category)}
      </a>
    );
  }

  private handleOfferCardClick = () => {
    const { isPopupsOpened, onPageJumped } = this.props.api.offerCard;

    if (isPopupsOpened()) {
      return;
    }

    onPageJumped('card_detail_in_newlist');
    window.open(this.props.offer.fullUrl, '_blank');
  }

  private handleOfferCardMouseEnter = () => this.setState({ galleryControlsHidden: false });

  private handleOfferCardMouseLeave = () => this.setState({ galleryControlsHidden: true });

  private handleTitleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    this.props.api.offerCard.onPageJumped('card_header_in_newlist');
    e.stopPropagation();
  }

  private handleSimilarClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const { offer: { kp, gaLabel }, api: { tracking }, position } = this.props;
    e.stopPropagation();

    if (gaLabel && tracking && tracking.trackMultiOfferClick) {
      if (kp) {
        trackSuburbanMultiOfferClick(kp.id, gaLabel, position);
      } else {
        tracking.trackMultiOfferClick(gaLabel);
      }
    }
  }

  private checkSimilarLink() {
    return Boolean(this.props.offer.similar && !this.props.jsonQuery.multi_id);
  }

  private handleHideFromPrintClick = () => {
    this.setState({
      isOfferHidden: true,
    });
  }

  private handleButtonMapClick = () => {

    this.setState({
      isMapActive: !this.state.isMapActive,
      isMapLoaded: true,
    });
  }

  private toggleCallbackModal = () => {
    this.setState({
      isCallbackModalOpened: !this.state.isCallbackModalOpened,
    });
  }

  private handleCommentClick = () => {
    if (this.state.isCommentAreaOpened) {
      return;
    }

    this.setState({
      isCommentAreaOpened: true,
    });
  }

  private handleCommentingCompletion = () => {
    if (!this.state.isCommentAreaOpened) {
      return;
    }

    this.setState({
      isCommentAreaOpened: false,
    });
  }

  private handleIdenticalOfferCardClick = (index: number) => {
    const { api: { tracking }} = this.props;

    if (tracking && tracking.trackIdenticalOfferClick) {
      tracking.trackIdenticalOfferClick(index);
    }
  }

  private handleShowAllIdenticalOffers = () => {
    const { api: { tracking }} = this.props;

    if (tracking && tracking.trackShowAllIdenticalOffers) {
      tracking.trackShowAllIdenticalOffers();
    }
  }

  private handleIdenticalOffersButtonClick = () => {
    const { areIdenticalOffersShown } = this.state;

    if (areIdenticalOffersShown) {
      this.handleHideIdenticalOffers();
    } else {
      this.handleShowIdenticalOffers();
    }
  }

  private handleShowIdenticalOffers = () => {
    const { api: { tracking }} = this.props;

    this.setState({ areIdenticalOffersShown: true });

    if (tracking && tracking.trackShowIdenticalOffers) {
      tracking.trackShowIdenticalOffers();
    }
  }

  private handleHideIdenticalOffers = () => {
    const { api: { tracking }} = this.props;

    this.setState({ areIdenticalOffersShown: false });

    if (tracking && tracking.trackHideIdenticalOffers) {
      tracking.trackHideIdenticalOffers();
    }
  }
}
